import type { CompositePrimaryKey, FieldFilter, ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import { isNonArrayRecord } from '#layers/base/public';
import { buildFilterFromItemKeys, createFieldFilter, CURRENT_USER_VARIABLE, FilterOperator } from '#layers/data-model/public';

/**
 * The four operators a related-item clause offers, mirroring the ID-style filter: identity
 * (`_eq` / `_in`) and their exclusions (`_neq` / `_nin`).
 */
export type RelatedItemOperator =
	| typeof FilterOperator.EQ
	| typeof FilterOperator.NEQ
	| typeof FilterOperator.IN
	| typeof FilterOperator.NIN;

/** The operator + selected item key(s) a clause's terminal match resolves to. */
export interface RelatedItemTerminalMatch {
	operator: RelatedItemOperator;
	keys: CompositePrimaryKey[];
}

export function isExclusionOperator(operator: RelatedItemOperator): boolean {
	return operator === FilterOperator.NEQ || operator === FilterOperator.NIN;
}

export function negateOperator(operator: RelatedItemOperator): RelatedItemOperator {
	switch (operator) {
		case FilterOperator.EQ: return FilterOperator.NEQ;
		case FilterOperator.NEQ: return FilterOperator.EQ;
		case FilterOperator.IN: return FilterOperator.NIN;
		case FilterOperator.NIN: return FilterOperator.IN;
		default: return operator;
	}
}

function toPositiveOperator(operator: RelatedItemOperator): RelatedItemOperator {
	if (operator === FilterOperator.NEQ) return FilterOperator.EQ;
	if (operator === FilterOperator.NIN) return FilterOperator.IN;
	return operator;
}

/**
 * Reads the terminal match — the value of the innermost relation hop — into an operator + key(s).
 * The shape encodes the operator (see the table in `buildTerminalMatch`). Returns `null` when the
 * shape is not a related-item identity/null match (e.g. a range operator on the key), so the row
 * falls through to the generic primitive editor.
 */
export function parseTerminalMatch(value: unknown, primaryKeyFields: ResolvedMonospacePrimitiveField[]): RelatedItemTerminalMatch | null {
	if (!isNonArrayRecord(value)) return null;

	const keys = Object.keys(value);

	// Composite exclusion: `{ _not: <positive> }` → the negated variant of the wrapped operator.
	if (keys.length === 1 && keys[0] === '_not') {
		const inner = parseIdentityMatch(value._not, primaryKeyFields);
		if (!inner) return null;
		return { operator: negateOperator(inner.operator), keys: inner.keys };
	}

	return parseIdentityMatch(value, primaryKeyFields);
}

function parseIdentityMatch(value: unknown, primaryKeyFields: ResolvedMonospacePrimitiveField[]): RelatedItemTerminalMatch | null {
	if (!isNonArrayRecord(value)) return null;

	if (primaryKeyFields.length === 1) {
		return parseSingleColumnMatch(value, primaryKeyFields[0]!.apiName);
	}

	// Composite "one of": `{ _or: [ <key>, … ] }`.
	if (Object.keys(value).length === 1 && Array.isArray(value._or)) {
		const items = value._or.map((entry) => parseCompositeKey(entry, primaryKeyFields));
		if (items.length === 0 || items.includes(null)) return null;
		return { operator: FilterOperator.IN, keys: items as CompositePrimaryKey[] };
	}

	// Composite "is": a single `{ pkA: { _eq }, pkB: { _eq } }` conjunction.
	const key = parseCompositeKey(value, primaryKeyFields);
	return key ? { operator: FilterOperator.EQ, keys: [key] } : null;
}

function parseSingleColumnMatch(value: Record<string, unknown>, pkApiName: string): RelatedItemTerminalMatch | null {
	// The `$CURRENT_USER` split emitted by `buildMembershipMatch`: `_or` of `_eq` + `_in` (membership),
	// `_and` of `_neq` + `_nin` (exclusion). Recombine the branch keys into one IN/NIN terminal.
	if (Array.isArray(value._or)) return parseSplitMembership(value._or, FilterOperator.IN, pkApiName);
	if (Array.isArray(value._and)) return parseSplitMembership(value._and, FilterOperator.NIN, pkApiName);

	if (Object.keys(value).length !== 1 || !isNonArrayRecord(value[pkApiName])) return null;

	const inner = value[pkApiName] as Record<string, unknown>;
	if (Object.keys(inner).length !== 1) return null;

	const operator = Object.keys(inner)[0]!;
	const operand = inner[operator];

	switch (operator) {
		case FilterOperator.EQ: return { operator: FilterOperator.EQ, keys: toSingleColumnKeys([operand], pkApiName) };
		case FilterOperator.NEQ: return { operator: FilterOperator.NEQ, keys: toSingleColumnKeys([operand], pkApiName) };
		case FilterOperator.IN: return parseMembershipMatch(FilterOperator.IN, operand, pkApiName);
		case FilterOperator.NIN: return parseMembershipMatch(FilterOperator.NIN, operand, pkApiName);
		// Any other operator (e.g. `_lt`) is a plain primitive filter on the key, not an identity match.
		default: return null;
	}
}

function parseSplitMembership(branches: unknown[], operator: typeof FilterOperator.IN | typeof FilterOperator.NIN, pkApiName: string): RelatedItemTerminalMatch | null {
	const keys: CompositePrimaryKey[] = [];

	const expectExclusion = operator === FilterOperator.NIN;

	for (const branch of branches) {
		if (!isNonArrayRecord(branch)) return null;
		const inner = parseSingleColumnMatch(branch, pkApiName);
		if (!inner || isExclusionOperator(inner.operator) !== expectExclusion) return null;
		keys.push(...inner.keys);
	}

	return keys.length > 0 ? { operator, keys } : null;
}

function parseMembershipMatch(operator: typeof FilterOperator.IN | typeof FilterOperator.NIN, operand: unknown, pkApiName: string): RelatedItemTerminalMatch | null {
	if (!Array.isArray(operand)) return null;

	const keys = toSingleColumnKeys(operand, pkApiName);
	return keys.length > 0 ? { operator, keys } : null;
}

function parseCompositeKey(value: unknown, primaryKeyFields: ResolvedMonospacePrimitiveField[]): CompositePrimaryKey | null {
	if (!isNonArrayRecord(value) || Object.keys(value).length !== primaryKeyFields.length) return null;

	const key: CompositePrimaryKey = {};

	for (const field of primaryKeyFields) {
		const inner = value[field.apiName];
		if (!isNonArrayRecord(inner) || Object.keys(inner).length !== 1 || !('_eq' in inner)) return null;
		key[field.apiName] = inner._eq as CompositePrimaryKey[string];
	}

	return key;
}

function toSingleColumnKeys(values: unknown[], pkApiName: string): CompositePrimaryKey[] {
	return values
		.filter((value) => value != null)
		.map((value) => ({ [pkApiName]: value as CompositePrimaryKey[string] }));
}

/**
 * Serializes an operator + key(s) into the terminal match (the value of the innermost relation hop):
 *
 * | operator      | single-column pk       | composite pk               |
 * | ------------- | ---------------------- | -------------------------- |
 * | is            | `{ id: { _eq } }`      | `{ pkA: { _eq }, … }`      |
 * | is one of     | `{ id: { _in } }`      | `{ _or: [ <key>, … ] }`    |
 * | is not        | `{ id: { _neq } }`     | `{ _not: { pkA: { _eq } } }`|
 * | isn't one of  | `{ id: { _nin } }`     | `{ _not: { _or: [ … ] } }` |
 */
export function buildTerminalMatch(
	operator: RelatedItemOperator,
	keys: CompositePrimaryKey[],
	primaryKeyFields: ResolvedMonospacePrimitiveField[],
	options: { positive?: boolean } = {},
): FieldFilter {
	if (keys.length === 0) return {} as FieldFilter;

	const effective = options.positive ? toPositiveOperator(operator) : operator;

	if (primaryKeyFields.length === 1) {
		const pkApiName = primaryKeyFields[0]!.apiName;
		const values = keys.map((key) => key[pkApiName]).filter((value) => value != null);

		switch (effective) {
			case FilterOperator.EQ: return createFieldFilter([pkApiName], FilterOperator.EQ, values[0] ?? null);
			case FilterOperator.NEQ: return createFieldFilter([pkApiName], FilterOperator.NEQ, values[0] ?? null);
			case FilterOperator.IN: return buildMembershipMatch(pkApiName, values, false);
			case FilterOperator.NIN: return buildMembershipMatch(pkApiName, values, true);
		}
	}

	const isSingle = effective === FilterOperator.EQ || effective === FilterOperator.NEQ;
	const isExclusion = effective === FilterOperator.NEQ || effective === FilterOperator.NIN;

	// A single "is" match is a bare conjunction (`{ pkA: {_eq}, pkB: {_eq} }`); a list "is one of" is
	// the `{ _or: [ … ] }` shape `buildFilterFromItemKeys` produces for composite keys.
	const positive: FieldFilter = isSingle
		? (keys[0] ? buildCompositeKeyMatch(keys[0], primaryKeyFields) : { _or: [] })
		: buildFilterFromItemKeys({ keys, primaryKeyFields }) as FieldFilter;

	return isExclusion ? { _not: positive } as FieldFilter : positive;
}

/**
 * Serializes a single-column membership (`_in`) or exclusion (`_nin`). `$CURRENT_USER` is a
 * single-value variable the engine only substitutes for `_eq`/`_neq`.
 */
function buildMembershipMatch(pkApiName: string, values: unknown[], exclude: boolean): FieldFilter {
	const listOp = exclude ? FilterOperator.NIN : FilterOperator.IN;
	const variables = values.filter((value) => value === CURRENT_USER_VARIABLE);
	const concrete = values.filter((value) => value !== CURRENT_USER_VARIABLE);

	if (variables.length === 0) return createFieldFilter([pkApiName], listOp, concrete);

	const eqOp = exclude ? FilterOperator.NEQ : FilterOperator.EQ;
	const clauses: FieldFilter[] = variables.map((variable) => createFieldFilter([pkApiName], eqOp, variable));
	if (concrete.length > 0) clauses.push(createFieldFilter([pkApiName], listOp, concrete));

	if (clauses.length === 1) return clauses[0]!;
	return (exclude ? { _and: clauses } : { _or: clauses }) as FieldFilter;
}

function buildCompositeKeyMatch(key: CompositePrimaryKey, primaryKeyFields: ResolvedMonospacePrimitiveField[]): FieldFilter {
	const conjunction: Record<string, unknown> = {};

	for (const field of primaryKeyFields) {
		conjunction[field.apiName] = { _eq: key[field.apiName] };
	}

	return conjunction as FieldFilter;
}
